import { CONSTANTS } from '@/utils/constants';
import { getPhanHeFromStorage, resolvePhanHeFromRoute } from '@/utils/phanHeStorage';
import { ROUTES } from '@/utils/pathRoutes';

/** Key zustand persist auth — khớp useAuthStore */
export const AUTH_STORAGE_KEY = 'TOPPION:auth';

/** Key zustand persist private-store — khớp usePrivateStore */
const PRIVATE_STORE_KEY = 'private-store';

export const LOGOUT_SIGNAL_KEY = 'TOPPION:logout-signal';
export const LOGIN_SIGNAL_KEY = 'TOPPION:login-signal';
const BROADCAST_CHANNEL_NAME = 'TOPPION-auth-channel';
export const TAB_ID_SESSION_KEY = 'TOPPION:tab-id';

/** localStorage keys dùng sync login/logout giữa tab — không xóa khi vào /login. */
export const AUTH_CROSS_TAB_LOCAL_KEYS = [
  AUTH_STORAGE_KEY,
  LOGIN_SIGNAL_KEY,
  LOGOUT_SIGNAL_KEY,
] as const;

/** sessionStorage keys dùng sync tab — không xóa khi vào /login. */
export const AUTH_CROSS_TAB_SESSION_KEYS = [TAB_ID_SESSION_KEY] as const;

type LogoutSignalPayload = {
  t: number;
  returnUrl: string | null;
};

type LoginSignalPayload = {
  t: number;
};

function readAuthSignalTime(key: string): number {
  if (typeof window === 'undefined') return 0;
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return 0;
    const parsed = JSON.parse(raw) as { t?: number };
    return typeof parsed?.t === 'number' ? parsed.t : 0;
  } catch {
    return 0;
  }
}

/** Logout mới hơn login → bỏ qua persist echo (tab kia còn token trong memory). */
export function isLogoutNewerThanLogin(): boolean {
  return readAuthSignalTime(LOGOUT_SIGNAL_KEY) > readAuthSignalTime(LOGIN_SIGNAL_KEY);
}

function snapshotStorage(storage: Storage, keys: readonly string[]): Record<string, string> {
  const snapshot: Record<string, string> = {};
  for (const key of keys) {
    const value = storage.getItem(key);
    if (value != null) snapshot[key] = value;
  }
  return snapshot;
}

function restoreStorage(storage: Storage, snapshot: Record<string, string>): void {
  for (const [key, value] of Object.entries(snapshot)) {
    storage.setItem(key, value);
  }
}

function removeStorageExcept(storage: Storage, keep: ReadonlySet<string>): void {
  const toRemove: string[] = [];
  for (let i = 0; i < storage.length; i += 1) {
    const key = storage.key(i);
    if (key && !keep.has(key)) toRemove.push(key);
  }
  toRemove.forEach((key) => storage.removeItem(key));
}

export type AuthCrossTabStorageSnapshot = {
  local: Record<string, string>;
  session: Record<string, string>;
};

/** Lưu key sync tab trước khi dọn session ở trang login. */
export function captureAuthCrossTabStorage(): AuthCrossTabStorageSnapshot {
  if (typeof window === 'undefined') return { local: {}, session: {} };
  return {
    local: snapshotStorage(localStorage, AUTH_CROSS_TAB_LOCAL_KEYS),
    session: snapshotStorage(sessionStorage, AUTH_CROSS_TAB_SESSION_KEYS),
  };
}

export function restoreAuthCrossTabStorage(snapshot: AuthCrossTabStorageSnapshot): void {
  if (typeof window === 'undefined') return;
  const local = { ...snapshot.local };
  if (isLogoutNewerThanLogin()) {
    delete local[AUTH_STORAGE_KEY];
    try {
      localStorage.removeItem(AUTH_STORAGE_KEY);
    } catch {
      // ignore
    }
  }
  restoreStorage(localStorage, local);
  restoreStorage(sessionStorage, snapshot.session);
}

/** Xóa local/session storage, không đụng key authCrossTabSync. */
export function clearStoragesPreservingAuthCrossTabKeys(): void {
  if (typeof window === 'undefined') return;
  removeStorageExcept(localStorage, new Set(AUTH_CROSS_TAB_LOCAL_KEYS));
  removeStorageExcept(sessionStorage, new Set(AUTH_CROSS_TAB_SESSION_KEYS));
}

function getTabId(): string {
  if (typeof window === 'undefined') return 'ssr';
  try {
    const existing = sessionStorage.getItem(TAB_ID_SESSION_KEY);
    if (existing) return existing;
    const next =
      (typeof crypto !== 'undefined' && 'randomUUID' in crypto && typeof crypto.randomUUID === 'function')
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    sessionStorage.setItem(TAB_ID_SESSION_KEY, next);
    return next;
  } catch {
    return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
  }
}

function parseAuthAccessToken(raw: string | null): string | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as { state?: { accessToken?: string | null } };
    return parsed?.state?.accessToken ?? null;
  } catch {
    return null;
  }
}

/**
 * Chỉ coi là login/logout khi accessToken thay đổi.
 * Bỏ qua cập nhật khác trong bucket auth (vd. bscFilter* từ FilterBSC) — tránh tab khác bị redirect dashboard.
 */
function resolveAuthStorageSessionChange(
  oldValue: string | null,
  newValue: string | null,
): AuthSessionChangedState | null {
  const oldToken = parseAuthAccessToken(oldValue);
  const newToken = parseAuthAccessToken(newValue);
  if (!oldToken && newToken) return 'login';
  if (oldToken && !newToken) return 'logout';
  if (oldToken && newToken && oldToken !== newToken) return 'login';
  return null;
}

function isLoginOrLogoutRoute(): boolean {
  if (typeof window === 'undefined') return false;
  const path = window.location.pathname.toLowerCase();
  return (
    path.includes(ROUTES.LOGIN.toLowerCase())
    || path.includes(ROUTES.LOGOUT.toLowerCase())
  );
}

export type AuthSessionChangedState = 'login' | 'logout';

function isAuthSessionChangedRoute(): boolean {
  if (typeof window === 'undefined') return false;
  const path = window.location.pathname.toLowerCase();
  return path.includes(ROUTES.AUTH_SESSION_CHANGED.toLowerCase());
}

function isAuthRoute(): boolean {
  if (typeof window === 'undefined') return false;
  const path = window.location.pathname.toLowerCase();
  return (
    path.includes(ROUTES.LOGIN.toLowerCase())
    || path.includes(ROUTES.LOGOUT.toLowerCase())
    || isAuthSessionChangedRoute()
  );
}

function isLogoutRoute(): boolean {
  if (typeof window === 'undefined') return false;
  const path = window.location.pathname.toLowerCase();
  return path.includes(ROUTES.LOGOUT.toLowerCase());
}

function isAuthPath(pathname: string): boolean {
  const lower = pathname.toLowerCase();
  return (
    lower.includes(ROUTES.LOGIN.toLowerCase())
    || lower.includes(ROUTES.LOGOUT.toLowerCase())
    || lower.includes(ROUTES.AUTH_SESSION_CHANGED.toLowerCase())
    || lower.includes(ROUTES.DB_RESTORE_LOCK.toLowerCase())
  );
}

/** Chuẩn hóa return URL nội bộ (chống open redirect). */
export function normalizeReturnUrl(raw: string | null | undefined): string | null {
  if (!raw) return null;
  let decoded = raw;
  try {
    decoded = decodeURIComponent(raw);
  } catch {
    decoded = raw;
  }
  if (!decoded.startsWith('/') || decoded.startsWith('//')) return null;
  const internalOrigin = 'https://return-url.invalid';
  try {
    const url = new URL(decoded, internalOrigin);
    return url.origin === internalOrigin
      ? `${url.pathname}${url.search}${url.hash}`
      : null;
  } catch {
    return null;
  }
}

/** Lấy path cần quay lại sau login từ asPath hiện tại. */
export function getReturnUrlFromPath(asPath: string): string | null {
  const pathOnly = asPath.split('?')[0]?.split('#')[0] ?? asPath;
  if (isAuthPath(pathOnly) || pathOnly === '/') return null;
  return normalizeReturnUrl(asPath);
}

export function buildLoginHref(returnUrl: string | null | undefined): string {
  const normalized = normalizeReturnUrl(returnUrl ?? null);
  if (!normalized) return ROUTES.LOGIN;
  return `${ROUTES.LOGIN}?return-url=${encodeURIComponent(normalized)}`;
}

export function buildLogoutHref(returnUrl: string | null | undefined): string {
  const normalized = normalizeReturnUrl(returnUrl ?? null);
  if (!normalized) return ROUTES.LOGOUT;
  return `${ROUTES.LOGOUT}?return-url=${encodeURIComponent(normalized)}`;
}

export function parseReturnUrlFromQuery(query: Record<string, string | string[] | undefined>): string | null {
  const raw = (query['return-url'] || query.returnUrl || query.backUrl) as string | string[] | undefined;
  const value = Array.isArray(raw) ? raw[0] : raw;
  return normalizeReturnUrl(value);
}

/** Tab hiện tại báo các tab khác đang đăng xuất */
export function notifyLogoutAcrossTabs(returnUrl: string | null = null): void {
  if (typeof window === 'undefined') return;

  const payload: LogoutSignalPayload = { t: Date.now(), returnUrl };

  try {
    localStorage.setItem(LOGOUT_SIGNAL_KEY, JSON.stringify(payload));
  } catch {
    // ignore quota / private mode
  }

  try {
    const channel = new BroadcastChannel(BROADCAST_CHANNEL_NAME);
    channel.postMessage({ type: 'logout', returnUrl, from: getTabId() });
    channel.close();
  } catch {
    // BroadcastChannel không hỗ trợ trên một số trình duyệt cũ
  }
}

/** Tab hiện tại báo các tab khác đã login */
export function notifyLoginAcrossTabs(): void {
  if (typeof window === 'undefined') return;

  const payload: LoginSignalPayload = { t: Date.now() };

  try {
    localStorage.setItem(LOGIN_SIGNAL_KEY, JSON.stringify(payload));
  } catch {
    // ignore
  }

  try {
    const channel = new BroadcastChannel(BROADCAST_CHANNEL_NAME);
    channel.postMessage({ type: 'login', from: getTabId() });
    channel.close();
  } catch {
    // ignore
  }
}

/** Cách xử lý cũ: chuyển tab khác sang /logout. Giữ lại để dùng lại khi cần. */
export function redirectToLogout(returnUrl: string | null = null): void {
  if (typeof window === 'undefined' || isAuthRoute()) return;

  const qs =
    returnUrl && returnUrl !== '/'
      ? `?return-url=${encodeURIComponent(returnUrl)}`
      : '';
  window.location.href = `${ROUTES.LOGOUT}${qs}`;
}

/**
 * Cách xử lý cũ: reload tab khi tab khác login.
 * Giữ lại để dùng lại khi cần.
 */
export function reloadOnLoginFromOtherTab(): void {
  if (typeof window === 'undefined' || isLogoutRoute()) return;
  window.location.reload();
}

/** URL trang hiện tại (dùng làm returnUrl khi chuyển sang /auth-session-changed). */
export function getCurrentPageReturnUrl(): string | null {
  if (typeof window === 'undefined') return null;
  return getReturnUrlFromPath(
    `${window.location.pathname}${window.location.search}${window.location.hash}`
  );
}

function isValidPostLoginReturnUrl(url: string | null | undefined): url is string {
  if (!url) return false;
  const pathOnly = url.split('?')[0]?.split('#')[0] ?? url;
  return !isAuthPath(pathOnly);
}

function parseReturnUrlFromSearch(search: string): string | null {
  if (!search) return null;
  const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
  const query: Record<string, string> = {};
  params.forEach((value, key) => {
    query[key] = value;
  });
  return parseReturnUrlFromQuery(query);
}

export function buildAuthSessionChangedHref(
  state: AuthSessionChangedState,
  returnUrl?: string | null
): string {
  const params = new URLSearchParams({ state });
  const normalized = normalizeReturnUrl(returnUrl ?? null);
  if (normalized && isValidPostLoginReturnUrl(normalized)) {
    params.set('returnUrl', normalized);
  }
  return `${ROUTES.AUTH_SESSION_CHANGED}?${params.toString()}`;
}

const resolveModuleLandingRoute = (phanHe?: string | null): string | null => {
  const modules = [...new Set(
    String(phanHe ?? '')
      .split(',')
      .map((item) => item.trim().toLowerCase())
      .filter(Boolean),
  )];
  if (modules.length > 1) return ROUTES.INTRO;
  if (modules.length !== 1) return null;
  if (modules[0] === CONSTANTS.PHAN_HE.VAN_HOA) return ROUTES.VAN_HOA.DASHBOARD;
  if (modules[0] === CONSTANTS.PHAN_HE.QUY_TRINH) return ROUTES.QUY_TRINH.DASHBOARD;
  return ROUTES.DASHBOARD;
};

/** URL quay lại sau auto-login: ưu tiên returnUrl; một phân hệ vào thẳng, nhiều phân hệ vào Intro. */
export function resolvePostLoginRoute(explicitReturnUrl?: string | null, phanHe?: string | null): string {
  const candidates: Array<string | null | undefined> = [
    normalizeReturnUrl(explicitReturnUrl ?? null),
  ];

  if (typeof window !== 'undefined') {
    candidates.push(parseReturnUrlFromSearch(window.location.search));
  }

  for (const candidate of candidates) {
    if (candidate && isValidPostLoginReturnUrl(candidate)) return candidate;
  }

  return resolveModuleLandingRoute(phanHe) ?? resolveDashboardRoute();
}

/** Dashboard theo phân hệ đang dùng (pathname hoặc localStorage). */
export function resolveDashboardRoute(): string {
  if (typeof window === 'undefined') return ROUTES.DASHBOARD;

  const routePhanHe = resolvePhanHeFromRoute(window.location.pathname);
  const savedPhanHe = getPhanHeFromStorage();
  const phanHe = routePhanHe ?? savedPhanHe ?? CONSTANTS.PHAN_HE.BSC;

  if (phanHe === CONSTANTS.PHAN_HE.VAN_HOA) return ROUTES.VAN_HOA.DASHBOARD;
  if (phanHe === CONSTANTS.PHAN_HE.QUY_TRINH) return ROUTES.QUY_TRINH.DASHBOARD;
  return ROUTES.DASHBOARD;
}

function normalizeLocationUrl(url: string): string {
  try {
    const parsed = new URL(url, window.location.origin);
    return `${parsed.pathname}${parsed.search}${parsed.hash}`;
  } catch {
    return url;
  }
}

/** Auth + private-store đã ghi localStorage (tab login persist xong). */
export function hasPersistedLoginData(): boolean {
  if (typeof window === 'undefined') return false;

  const token = parseAuthAccessToken(localStorage.getItem(AUTH_STORAGE_KEY));
  if (!token) return false;

  const raw = localStorage.getItem(PRIVATE_STORE_KEY);
  if (!raw) return false;

  try {
    const parsed = JSON.parse(raw) as {
      state?: { treeSelectDepartment?: { idSelect?: string | null } };
    };
    return Boolean(parsed?.state?.treeSelectDepartment?.idSelect);
  } catch {
    return false;
  }
}

/** Đợi tab login ghi xong auth + private-store trước khi tab khác redirect/reload. */
export function waitForPersistedLoginData(maxMs = 2000): Promise<void> {
  if (typeof window === 'undefined') return Promise.resolve();
  if (hasPersistedLoginData()) return Promise.resolve();

  return new Promise((resolve) => {
    const started = Date.now();
    const tick = () => {
      if (hasPersistedLoginData() || Date.now() - started >= maxMs) {
        resolve();
        return;
      }
      window.setTimeout(tick, 50);
    };
    window.setTimeout(tick, 50);
  });
}

function navigateToPostLoginRoute(explicitReturnUrl?: string | null): void {
  const target = resolvePostLoginRoute(explicitReturnUrl);
  const current = `${window.location.pathname}${window.location.search}${window.location.hash}`;

  if (normalizeLocationUrl(current) === normalizeLocationUrl(target)) {
    window.location.reload();
    return;
  }

  window.location.assign(target);
}

/** Tab khác login → quay URL đã lưu hoặc dashboard (reload hydrate auth từ localStorage). */
export function redirectToDashboardOnLoginFromOtherTab(explicitReturnUrl?: string | null): void {
  if (typeof window === 'undefined' || isLogoutRoute()) return;

  void waitForPersistedLoginData().then(() => {
    navigateToPostLoginRoute(explicitReturnUrl);
  });
}

/** Mất phiên auth (xóa TOPPION:auth / logout tab khác) → về login, giữ returnUrl. */
export function redirectToLoginOnSessionLost(returnUrl?: string | null): void {
  if (typeof window === 'undefined' || isAuthRoute()) return;
  window.location.replace(buildLoginHref(returnUrl ?? getCurrentPageReturnUrl()));
}

/** Tab khác login/logout → chuyển sang trang thông báo, user tự reload. */
export function redirectToAuthSessionChanged(state: AuthSessionChangedState): void {
  if (typeof window === 'undefined' || isLoginOrLogoutRoute()) return;

  if (state === 'logout') {
    redirectToLoginOnSessionLost(getCurrentPageReturnUrl());
    return;
  }

  if (isAuthSessionChangedRoute()) {
    // Trang /auth-session-changed tự sync state qua subscribeAuthSessionChangedPage.
    return;
  }

  window.location.href = buildAuthSessionChangedHref(state, getCurrentPageReturnUrl());
}

/**
 * Lắng nghe login/logout khi đang ở trang /auth-session-changed.
 * Khác subscribe*AcrossTabs: không bỏ qua trang thông báo.
 */
export function subscribeAuthSessionChangedPage(handlers: {
  onLogin: () => void;
  onLogout: () => void;
}): () => void {
  if (typeof window === 'undefined') return () => {};
  const selfTabId = getTabId();
  let debounceTimer: number | null = null;

  const scheduleSync = (action: 'login' | 'logout') => {
    if (debounceTimer != null) window.clearTimeout(debounceTimer);
    debounceTimer = window.setTimeout(() => {
      debounceTimer = null;
      if (action === 'login') handlers.onLogin();
      else handlers.onLogout();
    }, 80);
  };

  const onStorage = (e: StorageEvent) => {
    if (e.storageArea !== localStorage) return;

    if (e.key === LOGIN_SIGNAL_KEY && e.newValue) {
      scheduleSync('login');
      return;
    }

    if (e.key === LOGOUT_SIGNAL_KEY && e.newValue) {
      scheduleSync('logout');
      return;
    }

    if (e.key === AUTH_STORAGE_KEY) {
      const change = resolveAuthStorageSessionChange(e.oldValue, e.newValue);
      if (change) scheduleSync(change);
    }
  };

  let channel: BroadcastChannel | null = null;
  const onBroadcast = (ev: MessageEvent) => {
    if (ev.data?.from && ev.data.from === selfTabId) return;
    if (ev.data?.type === 'login') handlers.onLogin();
    if (ev.data?.type === 'logout') handlers.onLogout();
  };

  window.addEventListener('storage', onStorage);

  try {
    channel = new BroadcastChannel(BROADCAST_CHANNEL_NAME);
    channel.addEventListener('message', onBroadcast);
  } catch {
    channel = null;
  }

  return () => {
    if (debounceTimer != null) window.clearTimeout(debounceTimer);
    window.removeEventListener('storage', onStorage);
    channel?.removeEventListener('message', onBroadcast);
    channel?.close();
  };
}

/**
 * Lắng nghe logout từ tab khác (storage + BroadcastChannel).
 * Trả về hàm hủy đăng ký.
 */
export function subscribeLogoutAcrossTabs(
  onLogout: (returnUrl: string | null) => void
): () => void {
  if (typeof window === 'undefined') return () => {};
  const selfTabId = getTabId();

  const handleLogoutSignal = (returnUrl: string | null) => {
    if (isLoginOrLogoutRoute()) return;
    onLogout(returnUrl);
  };

  const onStorage = (e: StorageEvent) => {
    if (e.storageArea !== localStorage) return;

    if (e.key === LOGOUT_SIGNAL_KEY && e.newValue) {
      try {
        const payload = JSON.parse(e.newValue) as LogoutSignalPayload;
        handleLogoutSignal(payload.returnUrl ?? null);
      } catch {
        handleLogoutSignal(null);
      }
      return;
    }

    if (e.key === AUTH_STORAGE_KEY) {
      const change = resolveAuthStorageSessionChange(e.oldValue, e.newValue);
      if (change === 'logout') {
        handleLogoutSignal(null);
      }
    }
  };

  let channel: BroadcastChannel | null = null;
  const onBroadcast = (ev: MessageEvent) => {
    if (ev.data?.type === 'logout') {
      if (ev.data?.from && ev.data.from === selfTabId) return;
      handleLogoutSignal(ev.data.returnUrl ?? null);
    }
  };

  window.addEventListener('storage', onStorage);

  try {
    channel = new BroadcastChannel(BROADCAST_CHANNEL_NAME);
    channel.addEventListener('message', onBroadcast);
  } catch {
    channel = null;
  }

  return () => {
    window.removeEventListener('storage', onStorage);
    channel?.removeEventListener('message', onBroadcast);
    channel?.close();
  };
}

/**
 * Lắng nghe login từ tab khác (storage + BroadcastChannel).
 * Mục tiêu: reload tab để hydrate state auth mới.
 */
export function subscribeLoginAcrossTabs(onLogin: () => void): () => void {
  if (typeof window === 'undefined') return () => {};
  const selfTabId = getTabId();

  const handleLogin = () => {
    if (isLogoutRoute()) return;
    if (isLogoutNewerThanLogin()) return;
    onLogin();
  };

  const onStorage = (e: StorageEvent) => {
    if (e.storageArea !== localStorage) return;

    if (e.key === LOGIN_SIGNAL_KEY && e.newValue) {
      handleLogin();
      return;
    }

    // Fallback: accessToken mới xuất hiện trong persisted auth (tab khác login).
    if (e.key === AUTH_STORAGE_KEY) {
      const change = resolveAuthStorageSessionChange(e.oldValue, e.newValue);
      if (change === 'login') handleLogin();
    }
  };

  let channel: BroadcastChannel | null = null;
  const onBroadcast = (ev: MessageEvent) => {
    if (ev.data?.type === 'login') {
      if (ev.data?.from && ev.data.from === selfTabId) return;
      handleLogin();
    }
  };

  window.addEventListener('storage', onStorage);

  try {
    channel = new BroadcastChannel(BROADCAST_CHANNEL_NAME);
    channel.addEventListener('message', onBroadcast);
  } catch {
    channel = null;
  }

  return () => {
    window.removeEventListener('storage', onStorage);
    channel?.removeEventListener('message', onBroadcast);
    channel?.close();
  };
}
